// Packages
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { FormattedMessage, useIntl } from 'react-intl';
// Components
import ModalCloseIcon from "../../components/modalCloseIcon";
import OTPInput from "../../mobileComponents/otpInput";
import DialingCodeSelector from "../../mobileComponents/dialingCodeSelector";
import OtpTimer from "../../components/otpTimer";
// Icons, Images etc.
import { faChevronCircleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Redux Operations
import { userOperations } from "../../state/features/user";
import { loaderOperations } from "../../state/features/loader";
import { cartOperations } from "../../state/features/cart";
import { errorOperations } from "../../state/features/error";
import { usePromotionsContext } from "../../hooks/usePromotionsContext";
import { getSubdomainPath } from "../../helpers/pathUtils";

interface IMobileVerificationProps {
  onClose: any;
  postRegistrationPage: string;
  showLoader: any;
  setShowEmailOtp: (value: boolean) => void;
  showEmailOtp: boolean;
  userPayload: any;
  company: any;
  smsDisabled: boolean;
  cartItems: any[];
  signIn: (data: any, token: string) => void;
  sendOtp: (token: string, companyId?: number) => any;
  resendOtp: (token: string, companyId?: number) => any;
  sendOtpViaEmail: (payload: any, token: string, companyId?: number) => any;
  resendOtpViaEmail: (payload: any, token: string, companyId?: number) => any;
  validateCart(cart: any[]): Promise<any>;
  hideLoader(): void;
  updateError(error: any): void;
}

function MobileVerification(props: IMobileVerificationProps) {
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("" as boolean | string);
  const [redirectURL, setRedirectURL] = useState("" as null | string);
  const [showOtp, setShowOtp] = useState(true);
  const [otpTimeout, setOtpTimeout] = useState(false);
  const [newOtpRequested, setNewOtpRequested] = useState(false);
  const [sentToPhoneInstead, setSentToPhoneInstead] = useState(false);
  const userRef = useRef<null|any>(null);
  const { cartVerificationPayload } = usePromotionsContext();
  const intl = useIntl();

  const csrfToken = document.querySelector('[name=csrf-token]').content;

  // The server decides where the OTP goes (an existing phone-verified
  // customer stays on their phone while SMS is on). Follow it so the page
  // never says "check your email" when the code went to the phone.
  const followOtpChannel = (response: any) => {
    if (response.error) return;
    if (props.showEmailOtp && response.payload?.data?.channel === "sms") {
      props.setShowEmailOtp(false);
      setSentToPhoneInstead(true);
    }
  };

  // Message for the error id the sign-in API returns.
  const otpErrorMessage = (errorId: any) => {
    switch (errorId) {
      case "otp_attempts_exhausted":
        return (
          <FormattedMessage
            id="registration.otp.attempts_exhausted"
            defaultMessage="Too many incorrect attempts. Please request a new OTP."
          />
        );
      case "otp_expired":
        return (
          <FormattedMessage
            id="registration.otp.expired"
            defaultMessage="This code has expired. Please request a new one."
          />
        );
      case "otp_locked":
        return (
          <FormattedMessage
            id="registration.otp.locked"
            defaultMessage="Too many attempts. Please try again later."
          />
        );
      default:
        return (
          <FormattedMessage
            id="registration.otp.incorrect_otp"
            defaultMessage="Incorrect OTP."
          />
        );
    }
  };

  const showOtpRequestError = (response: any) => {
    if (response.error?.response?.status !== 429) return;

    props.updateError({
      title: intl.formatMessage({ id: "error.oops", defaultMessage: "Oops!" }),
      message:
        response.error.response.data?.id === "otp_locked"
          ? intl.formatMessage({
              id: "registration.otp.locked",
              defaultMessage: "Too many attempts. Please try again later.",
            })
          : intl.formatMessage({
              id: "registration.otp.too_many_requests",
              defaultMessage: "Too many attempts. Please try again in a few minutes.",
            }),
    });
  };

  useEffect(() => {
    localStorage.setItem("otpStatus", "mobile");
    sendOtp();
  }, []);

  const sendOtp = () => {
    const handlePostOtpRequest = (response: any) => {
      followOtpChannel(response);
      if (response.error) {
        showOtpRequestError(response);
        handleClose();
      }
    };
    if (!props.showEmailOtp) {
      props.sendOtp(csrfToken, props.company?.id).then(handlePostOtpRequest);
    } else {
      props
        .sendOtpViaEmail({ email: props.userPayload.email }, csrfToken, props.company?.id)
        .then(handlePostOtpRequest);
    }
  };

  const resendOtp = () => {
    setOtpError("");
    setShowOtp(false);
    const handlePostOtpRequest = (response: any) => {
      followOtpChannel(response);
      if (response.error) {
        showOtpRequestError(response);
        handleClose();
      } else {
        setShowOtp(true);
        setOtpTimeout(false);
      }
    };
    if (!props.showEmailOtp) {
      props.resendOtp(csrfToken, props.company?.id).then((response: any) => {
        if (!response.error) {
          setNewOtpRequested(true);
        }
        handlePostOtpRequest(response);
      });
    } else {
      props
        .resendOtpViaEmail({ email: props.userPayload.email }, csrfToken, props.company?.id)
        .then((response: any) => {
          if (!response.error) {
            setNewOtpRequested(true);
          }
          handlePostOtpRequest(response);
        });
    }
  };

  const handleClose = () => {
    props.onClose(), 1000;
  };

  const submitOtp = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let userData = { otp: otp, marketing_consent: props.userPayload.marketing_consent };
    if (props.showEmailOtp && props.userPayload.email) {
      userData['email'] = props.userPayload.email;
    }

    props.signIn(userData, csrfToken).then((response: any) => {
      if (!response.error) {
        userRef.current = response.payload.data;
        onUserActionComplete();
      } else if (response.error.response.data.message) {
        setOtpError(response.error.response.data.id || "otp_error");
      }
    });
  };

  const redirectToPostRegistrationPage = () => {
    if (props.postRegistrationPage) {
      localStorage.removeItem("postRegistrationPage");
      localStorage.removeItem("otpStatus");
      const redirectPath = getSubdomainPath(props.postRegistrationPage);
      setRedirectURL(redirectPath);
    } else {
      const homePath = getSubdomainPath("/m");
      setRedirectURL(homePath);
    }
  };

  const validateCartAndRedirect = async () => {
    props.showLoader();
    const customerId = userRef.current.id;
    const customerName = userRef.current.name ?? "";
    const customerEmail = userRef.current.email ?? "";
    const updatedPayload = {
      ...cartVerificationPayload,
      customer_id: customerId,
      user_name: customerName,
      customer_email: customerEmail,
    }; //updating with user details
    try {
      const result = await props.validateCart(updatedPayload);
      if (!result.error && result.payload.data.validated) {
        redirectToPostRegistrationPage();
      } else {
        props.hideLoader();
        handleCartValidationFailure();
      }
    } catch (error) {
      handleCartValidationFailure();
    } finally {
      props.hideLoader();
    }
  };

  const handleCartValidationFailure = () => {
    handleClose();
    props.hideLoader();
    props.updateError({
      title: intl.formatMessage({ id: "error.oops", defaultMessage: "Oops!" }),
      message: intl.formatMessage({
        id: "error.something_went_wrong",
        defaultMessage: "Something went wrong. Please try again later.",
      }),
    });
  };

  const onUserActionComplete = () => {
    localStorage.removeItem("userEmail");
    props.showLoader();
    if (props.cartItems.length > 0) {
      validateCartAndRedirect();
    } else {
      redirectToPostRegistrationPage();
    }
  };

  const handleOTPInput = (otp: any) => {
    setOtpError("");
    setOtp(otp);
  };

  const switchToEmailVerification = () => {
    const handlePostOtpRequest = (response: any) => {
      followOtpChannel(response);
      if (response.error) {
        showOtpRequestError(response);
        handleClose();
      }
    };

    props.setShowEmailOtp(true);

    if (props.userPayload.email) {
      props
        .sendOtpViaEmail({ email: props.userPayload.email }, csrfToken, props.company?.id)
        .then(handlePostOtpRequest);
    } else {
      handleClose();
    }
  }

  return (
    <React.Fragment>
      {redirectURL ? (
        <Redirect to={redirectURL} />
      ) : (
        <div className="login-screen otp-verify-screen">
          <div className="user-login px-3 py-3 mb-4 mt-4">
            <h1 className="header">
              <FormattedMessage
                id="registration.otp.enter_the_otp_code"
                defaultMessage="Enter the OTP Code"
              />
            </h1>
            <div>
              <h3 className="mt-2 mb-3 text-center">
                {!props.showEmailOtp ? (
                  <FormattedMessage
                    id="registration.otp.enter_code_sent_to_phone"
                    defaultMessage="Please enter the code we have sent to <pTag>{mobileNumber}</pTag>."
                    values={{
                      mobileNumber: `+${props.userPayload.dialing_code} ${props.userPayload.phone_number}`,
                      pTag: (mobileNumber: any) => (
                        <p className="font-weight-bold">{mobileNumber}</p>
                      ),
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id="registration.otp.enter_code_sent_to_email"
                    defaultMessage="Please enter code we sent to <pTag>{email}</pTag>."
                    values={{
                      email: `${props.userPayload.email}`,
                      pTag: (email: any) => (
                        <p className="font-weight-bold">{email}</p>
                      ),
                    }}
                  />
                )}
              </h3>
              {sentToPhoneInstead && (
                <p className="otp-resent-info">
                  <FormattedMessage
                    id="registration.otp.sent_to_phone_instead"
                    defaultMessage="For your security, we sent the code to your registered phone number."
                  />
                </p>
              )}
              {newOtpRequested && (
                <p className="otp-resent-info">
                  <FormattedMessage
                    id="registrations.otp.resend_requested"
                    defaultMessage="A new OTP has been sent"
                  />
                </p>
              )}
              <form className="text-center" onSubmit={submitOtp}>
                {showOtp ? (
                  <OTPInput
                    dir="ltr"
                    autoFocus
                    length={4}
                    className="otpContainer"
                    inputClassName="otpInput form-control"
                    onChangeOTP={(otp: any) => handleOTPInput(otp)}
                  />
                ) : null}
                {otpError ? (
                  <div className="input-label text-center text-danger">
                    {otpErrorMessage(otpError)}
                  </div>
                ) : null}
                <button className="btn button btn-primary w-100 py-2 mt-4 mb-3">
                  <FormattedMessage
                    id="global.continue"
                    defaultMessage="Continue"
                  />
                  <span className="ml-3">
                    <FontAwesomeIcon icon={faChevronCircleRight} />
                  </span>
                </button>
                <p className="other-actions otp-resend">
                  <FormattedMessage
                    id="registration.otp.receive_question"
                    defaultMessage="Didn't receive the OTP?"
                  />
                </p>
                {!otpTimeout ? (
                  <p className="other-actions otp-wait">
                    <OtpTimer setOtpTimeout={setOtpTimeout} />
                  </p>
                ) : (
                  <p
                    className="other-actions otp-resend-action py-2"
                    onClick={resendOtp}
                  >
                    <FormattedMessage
                      id="registrations.otp.resend_button"
                      defaultMessage="Resend"
                    />
                  </p>
                )}
              </form>
            </div>
          </div>
          {"email" in props.company.otp_settings &&
            props.company.otp_settings.email &&
      props.userPayload.email_otp_allowed !== false &&
            !props.showEmailOtp ? (
              <p
                className="other-actions change-otp-mode"
                onClick={() => switchToEmailVerification()}
              >
                <span>
                  <FormattedMessage
                    id="registration.otp.sent_otp_to_my_email"
                    defaultMessage="Send OTP to my email"
                  />
                </span>
              </p>
            ) : !props.smsDisabled && props.showEmailOtp ? (
              <p
                className="other-actions change-otp-mode"
                onClick={() => {
                  handleClose();
                  props.setShowEmailOtp(false);
                }}
              >
                <span>
                  <FormattedMessage
                    id="registrations.otp.receive_otp_via_sms"
                    defaultMessage="Receive OTP via SMS"
                  />
                </span>
              </p>
            ) : null}
        </div>
      )}
    </React.Fragment>
  );
}

const mapStateToProps = (state: any) => {
  let company = state.company;
  let smsDisabled = company?.sms_disabled || false;
  let cartItems = state.cart?.items || [];

  return { 
    company,
    smsDisabled,
    cartItems
  };
};

const mapDispatchToProps = {
  sendOtp: userOperations.sendOtp,
  resendOtp: userOperations.resendOtp,
  signIn: userOperations.signIn,
  showLoader: loaderOperations.showLoader,
  sendOtpViaEmail: userOperations.sendOtpViaEmail,
  resendOtpViaEmail: userOperations.resendOtpViaEmail,
  validateCart: cartOperations.validateCart,
  hideLoader: loaderOperations.hideLoader,
  updateError: errorOperations.updateError,
};

export default connect(mapStateToProps, mapDispatchToProps)(MobileVerification);
